import { useState, useEffect, useCallback, useMemo } from 'react';
import { getInsuranceCompanies } from '../api/insurance';
import type { InsuranceCompany } from '../types';

interface UseInsuranceCompaniesOptions {
  isActive?: boolean;
}

/**
 * Hook for fetching and managing insurance companies list
 */
export function useInsuranceCompanies(options?: UseInsuranceCompaniesOptions) {
  const [data, setData] = useState<InsuranceCompany[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Memoize options to avoid unnecessary re-renders
  const optionsKey = useMemo(() => JSON.stringify(options), [options]);

  const fetchCompanies = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const companies = await getInsuranceCompanies(options?.isActive);
      setData(companies);
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to fetch insurance companies');
    } finally {
      setIsLoading(false);
    }
  }, [options?.isActive]);

  useEffect(() => {
    fetchCompanies();
  }, [optionsKey, fetchCompanies]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchCompanies,
  };
}
